/**
 * User Skill Level Detection Service
 * Determines user expertise based on activity and adapts UI accordingly
 */

// Skill levels
export const SKILL_LEVELS = {
  BEGINNER: 'beginner',      // 0-5 campaigns, 0-10 content pieces
  INTERMEDIATE: 'intermediate', // 6-20 campaigns, 11-50 content pieces
  ADVANCED: 'advanced'        // 21+ campaigns, 51+ content pieces
};

// Activity thresholds
const THRESHOLDS = {
  campaigns: {
    beginner: 5,
    intermediate: 20
  },
  content: {
    beginner: 10,
    intermediate: 50
  },
  analytics: {
    beginner: 5,
    intermediate: 15
  }
};

/**
 * Calculate user skill level based on activity
 * @param {Object} userActivity - User's activity data
 * @returns {string} Skill level (beginner, intermediate, advanced)
 */
export const calculateSkillLevel = (userActivity = {}) => {
  const {
    totalCampaigns = 0,
    totalContent = 0,
    totalAnalytics = 0,
    accountAge = 0, // days since account creation
    lastLoginDays = 0 // days since last login
  } = userActivity;

  // Calculate scores for different areas
  const campaignScore = getCampaignScore(totalCampaigns);
  const contentScore = getContentScore(totalContent);
  const analyticsScore = getAnalyticsScore(totalAnalytics);
  const engagementScore = getEngagementScore(accountAge, lastLoginDays);

  // Weighted average (campaigns are most important)
  const totalScore = (
    campaignScore * 0.4 +
    contentScore * 0.3 +
    analyticsScore * 0.2 +
    engagementScore * 0.1
  );

  // Determine skill level
  if (totalScore < 33) return SKILL_LEVELS.BEGINNER;
  if (totalScore < 66) return SKILL_LEVELS.INTERMEDIATE;
  return SKILL_LEVELS.ADVANCED;
};

/**
 * Get campaign experience score (0-100)
 */
const getCampaignScore = (total) => {
  if (total <= THRESHOLDS.campaigns.beginner) return (total / THRESHOLDS.campaigns.beginner) * 33;
  if (total <= THRESHOLDS.campaigns.intermediate) return 33 + ((total - THRESHOLDS.campaigns.beginner) / (THRESHOLDS.campaigns.intermediate - THRESHOLDS.campaigns.beginner)) * 33;
  return 100;
};

/**
 * Get content creation score (0-100)
 */
const getContentScore = (total) => {
  if (total <= THRESHOLDS.content.beginner) return (total / THRESHOLDS.content.beginner) * 33;
  if (total <= THRESHOLDS.content.intermediate) return 33 + ((total - THRESHOLDS.content.beginner) / (THRESHOLDS.content.intermediate - THRESHOLDS.content.beginner)) * 33;
  return 100;
};

/**
 * Get analytics usage score (0-100)
 */
const getAnalyticsScore = (total) => {
  if (total <= THRESHOLDS.analytics.beginner) return (total / THRESHOLDS.analytics.beginner) * 33;
  if (total <= THRESHOLDS.analytics.intermediate) return 33 + ((total - THRESHOLDS.analytics.beginner) / (THRESHOLDS.analytics.intermediate - THRESHOLDS.analytics.beginner)) * 33;
  return 100;
};

/**
 * Get engagement score based on account age and activity (0-100)
 */
const getEngagementScore = (accountAge, lastLoginDays) => {
  // Penalize new accounts and inactive users
  if (accountAge < 7) return 0; // Less than a week old
  if (lastLoginDays > 30) return 20; // Inactive for a month
  if (lastLoginDays > 7) return 50; // Inactive for a week
  
  // Reward active, established users
  if (accountAge > 90) return 100; // 3+ months
  if (accountAge > 30) return 80;  // 1+ month
  return 60; // 1-4 weeks
};

/**
 * Get UI preferences for skill level
 * @param {string} skillLevel - User's skill level
 * @returns {Object} UI preferences
 */
export const getUIPreferences = (skillLevel) => {
  switch (skillLevel) {
    case SKILL_LEVELS.BEGINNER:
      return {
        showWizards: true,
        showTooltips: true,
        showOnboarding: true,
        showAdvancedOptions: false,
        defaultView: 'guided',
        showHelpText: true,
        enableQuickActions: false,
        progressiveDisclosure: true
      };

    case SKILL_LEVELS.INTERMEDIATE:
      return {
        showWizards: false,
        showTooltips: true,
        showOnboarding: false,
        showAdvancedOptions: true,
        defaultView: 'standard',
        showHelpText: false,
        enableQuickActions: true,
        progressiveDisclosure: false
      };

    case SKILL_LEVELS.ADVANCED:
      return {
        showWizards: false,
        showTooltips: false,
        showOnboarding: false,
        showAdvancedOptions: true,
        defaultView: 'advanced',
        showHelpText: false,
        enableQuickActions: true,
        progressiveDisclosure: false
      };

    default:
      return getUIPreferences(SKILL_LEVELS.BEGINNER);
  }
};

/**
 * Get welcome message based on skill level
 */
export const getWelcomeMessage = (skillLevel, userName = 'there') => {
  switch (skillLevel) {
    case SKILL_LEVELS.BEGINNER:
      return `Welcome, ${userName}! Let's build your first campaign together. I'll guide you through each step.`;
    
    case SKILL_LEVELS.INTERMEDIATE:
      return `Welcome back, ${userName}! Ready to create something great?`;
    
    case SKILL_LEVELS.ADVANCED:
      return `Hey ${userName}, what are we building today?`;
    
    default:
      return `Welcome, ${userName}!`;
  }
};

/**
 * Get suggested next action based on skill level
 */
export const getSuggestedAction = (skillLevel, context = 'campaigns') => {
  const suggestions = {
    beginner: {
      campaigns: {
        label: "Let's build your first audience!",
        description: "I'll walk you through creating a targeted audience step-by-step",
        action: 'start-wizard'
      },
      content: {
        label: "Create your first blog post",
        description: "Use our AI-powered blog generator to get started",
        action: 'start-wizard'
      },
      analytics: {
        label: "See your campaign performance",
        description: "I'll show you the most important metrics",
        action: 'view-dashboard'
      }
    },
    intermediate: {
      campaigns: {
        label: "Create high-intent audience",
        description: "Use last quarter's data to find your best customers",
        action: 'quick-create'
      },
      content: {
        label: "Repurpose top content",
        description: "Turn your best blog post into social media content",
        action: 'quick-create'
      },
      analytics: {
        label: "Optimize underperforming campaigns",
        description: "AI found 3 campaigns that need attention",
        action: 'view-insights'
      }
    },
    advanced: {
      campaigns: {
        label: "Quick create campaign",
        description: "All options, no wizard",
        action: 'advanced-create'
      },
      content: {
        label: "Bulk content generation",
        description: "Generate 10 pieces at once",
        action: 'bulk-create'
      },
      analytics: {
        label: "Custom attribution model",
        description: "Build your own multi-touch attribution",
        action: 'advanced-analytics'
      }
    }
  };

  return suggestions[skillLevel]?.[context] || suggestions.beginner[context];
};

export default {
  SKILL_LEVELS,
  calculateSkillLevel,
  getUIPreferences,
  getWelcomeMessage,
  getSuggestedAction
};
